<template>
  <div id="app" :class="appShellClass" :style="appShellStyle">
    <template v-if="shouldShowHeader">
      <SitterOnboardingNav v-if="onboardingLayout" />
      <GlobalNav v-else-if="isParent" />
      <SitterNav v-else-if="isSitter" />
      <PublicNav v-else />
    </template>
    <EmailVerificationBanner
      v-if="shouldMountEmailVerificationBanner"
      @visibility-change="emailVerificationBannerVisible = $event"
      @feedback-visible="emailVerificationFeedbackVisible = $event"
      @offset-height="emailVerificationBannerHeightPx = $event"
    />
    <ContentfulBanner
      v-if="shouldShowBanner"
      :segment-event-name="'UpgradePricing_PostLogin_Banner'"
      :offset-for-email-verification-banner="emailVerificationBannerVisible"
    />
    <div class="flex-1">
      <router-view />
    </div>
    <PoppinsCondensedFooter v-if="shouldShowFooter" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import PoppinsCondensedFooter from '@/components/condensed_footer/PoppinsCondensedFooter.vue';
import ContentfulBanner from '@/components/contentful_banner/ContentfulBanner.vue';
import EmailVerificationBanner from '@/components/email_verification_banner/EmailVerificationBanner.vue';
import GlobalNav from '@/components/nav/GlobalNav.vue';
import PublicNav from '@/components/nav/PublicNav.vue';
import SitterNav from '@/components/nav/SitterNav.vue';
import SitterOnboardingNav from '@/components/navigation/SitterOnboardingNav.vue';
import { readMetaBoolean } from '@/lib/page';
import shouldShowContentfulBanner from '@/helpers/bannerDisplayLogic';

defineOptions({ name: 'App' });

const route = useRoute();
const store = useStore();

const emailVerificationBannerVisible = ref(false);
const emailVerificationFeedbackVisible = ref(false);
const emailVerificationBannerHeightPx = ref(0);

const account = computed(() => store.getters['account/getAccount']);
const isParent = computed(() => account.value?.isParent() ?? false);
const isSitter = computed(() => account.value?.isSitter() ?? false);
const nativeMobileApp = computed(() => readMetaBoolean('native_mobile_app'));
const shouldShowHeader = computed(() => {
  const hideFromMeta = route.matched?.some((record) => record.meta?.hideMainHeader);
  return !hideFromMeta && !nativeMobileApp.value;
});
const shouldShowBanner = computed(() => {
  // EV banner replaces the value-plans (Contentful) banner until the user verifies.
  if (emailVerificationBannerVisible.value) {
    return false;
  }
  return shouldShowContentfulBanner(account.value, route.path);
});
const shouldMountEmailVerificationBanner = computed(() => shouldShowHeader.value && (isParent.value || isSitter.value));
const shouldShowFooter = computed(() => !route.matched?.some((record) => record.meta?.shouldHideFooter));
const onboardingLayout = computed(() => route.matched?.some((record) => record.meta?.sitterOnboardingLayout));

// Padding stays nav-only. Banner px lives in --banner-offset so Tailwind classes stay static;
// #app adds the 48px/80px nav when it publishes --nav-height.
const appShellClass = computed(() => (shouldShowHeader.value ? 'pt-48 md-1:pt-80' : ''));
const appShellStyle = computed(() => {
  if (!shouldShowHeader.value) {
    return { '--nav-height': '0px' };
  }

  let bannerPx = 0;
  if (emailVerificationBannerVisible.value) {
    // Fallback matches leading-10 + py-8; doubles when resend feedback is stacked.
    const fallbackPx = emailVerificationFeedbackVisible.value ? 112 : 56;
    bannerPx = emailVerificationBannerHeightPx.value || fallbackPx;
  }
  return { '--banner-offset': `${bannerPx}px` };
});

watch(shouldMountEmailVerificationBanner, (isMounted) => {
  if (!isMounted) {
    emailVerificationBannerVisible.value = false;
    emailVerificationFeedbackVisible.value = false;
    emailVerificationBannerHeightPx.value = 0;
  }
});
</script>

<style lang="scss" scoped>
#app {
  display: flex;
  flex-direction: column;
  min-height: calc(100dvh);
  --nav-height: calc(48px + var(--banner-offset, 0px));

  @media (min-width: 768px) {
    --nav-height: calc(80px + var(--banner-offset, 0px));
  }
}
</style>
